// Libs
import when from 'when'
import Emitter from 'event-emitter'
import glmat from 'gl-matrix'

// Strcutral
import SceneAbs from '../abstract/scene'
import Camera from '../utils/camera'
import TextureLoader from '../utils/textureLoaderObject'
import Node from '../abstract/node'
import Manifest from '../manifest'
import Mouse from '@/js/utils/mouse'

// Entities
import WordFactory from '../entities/wordsFactory'
import Plane from '../entities/plane'

// Post
import PostShake from '../postProcess/shakePostProcess'
import PostMarbling from '../postProcess/marblingDisplacement'
import ShakeMarbling from '../postProcess/shakeMarbling'

// Utils
import RAF from '@/js/utils/raf'
import DebugController from '@/glxp/debug/debugController'

let mat4 = glmat.mat4
let vec3 = glmat.vec3
let vec2 = glmat.vec2

const _VEC2 = vec2.create()
const MARGIN_BOTTOM = 100

var _emitter = {}
Emitter(_emitter)

class Scene extends SceneAbs {
  constructor (container, manager = null) {
    super(container, manager)

    this.manager = manager
    this.camera = new Camera(this, 30)
    this.camera.node.position[2] = -6
    this.shakeFactor = 0
    this.shakeFactorTarget = 0
    this.step = 0
    this.stepTarget = 0
    this.cursor = vec2.create()
    this.cursorTarget = vec2.create()
    this.lastCursor = vec2.create()
    this.velocity = vec2.create()
    this.clearColor = [1, 1, 1, 1]
    this.marginBottom = MARGIN_BOTTOM * this.dpr

    this.mobile = false
    this.loaded = false

    this.textureLoader = new TextureLoader(this)

    this.word = new Plane(this, 'shake it', 'shake', 'unlit')
    this.word.node.scale[0] = 4
    this.word.node.position[1] = -0.25
    this.postShake = new PostShake(this)

    this.config = {
      shake_damping: { value: 0.3, range: [0, 0.1] },
      cursor_damping: { value: 0.1, range: [0, 0.1] },
      mobile_cursor_damping: { value: 0.5, range: [0, 1] },
      step_damping: { value: 0.02, range: [0, 0.1] },
      shake_threshold: { value: 0.05, range: [0, 1] },
      shake_position: { value: -0, range: [-1, 1] },
      shake_scale: { value: 1.5, range: [-10, 10] },
      shake_mobile_scale: { value: -10, range: [-10, 10] },
      power_mobile_shake: { value: 0.2, range: [0, 1] }
    }

    DebugController.addConfig(this.config, 'HomeScene')
  }

  onLoaded () {
    this.active = true
    this._emitter.emit('loaded')
  }

  start () {
    // this.shakeFactor = .05
  }

  loadColor (color) {
    this.postShake.color = {
      r: color[0],
      g: color[1],
      b: color[2]
    }
  }

  load () {
    let loadables = []
    for (const key in Manifest.home) {
      if (Manifest.home.hasOwnProperty(key)) {
        const element = Manifest.home[key].url
        loadables.push(this.textureLoader.load(element, key, Manifest.home[key].options))
      }
    }

    when.all(loadables).then(() => {
      this.onLoaded()
    })

    let percent = 0
    for (let i = 0; i < loadables.length; i++) {
      loadables[i].then(() => {
        percent++
        this._emitter.emit('progress', Math.round((percent / loadables.length) * 100))
      })
    }
  }

  applyDefaultState () {
    let gl = this.gl
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
    gl.enable(gl.BLEND)
    gl.enable(gl.DEPTH_TEST)
    // gl.disable(gl.CULL_FACE)
  }

  // Pre-render
  preRender () {
    let gl = this.gl

    this.velocity[0] = this.cursorTarget[0] - this.lastCursor[0]
    this.velocity[1] = this.cursorTarget[1] - this.lastCursor[1]
    this.lastCursor[0] = this.cursorTarget[0]
    this.lastCursor[1] = this.cursorTarget[1]

    vec2.sub(_VEC2, this.cursorTarget, this.cursor)
    vec2.scale(_VEC2, _VEC2, this.config.mobile_cursor_damping.value)
    vec2.add(this.cursor, this.cursor, _VEC2)

    this.camera.updateProjection(this.width / this.height)
    this.shakeFactorTarget = Math.max(vec2.length(this.velocity), 0.03)
    let tmp = this.shakeFactorTarget - this.shakeFactor
    tmp *= this.config.shake_damping.value
    this.shakeFactor += tmp

    if (this.shakeFactor > this.config.shake_threshold.value) {
      this.stepTarget = 1
    } else {
      this.stepTarget = 0
    }
    tmp = this.stepTarget - this.step
    tmp *= this.config.step_damping.value
    this.step += tmp
    let shakePos = window.innerWidth > 813 ? 1 : this.config.shake_mobile_scale.value

    // this.word.node.position[1] = this.config.shake_position.value
    this.word.node.position[1] = 0
    this.word.node.position[2] = shakePos
    this.word.node.needUpdate = true

    this.camera.node.position[2] = -6 + (Math.easing.easeInQuart(this.step) * 4)
    this.camera.node.needUpdate = true
  }

  // Render
  render () {
    if (!this.active) { return }

    this.time += RAF.dt / 1000
    this.dt = RAF.dt

    let gl = this.gl

    gl.clearColor(this.clearColor[0], this.clearColor[1], this.clearColor[2], this.clearColor[3])
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT)
    gl.colorMask(true, true, true, true)

    this.preRender()
    gl.viewport(0, 0, this.width, this.height)

    // Render Time
    this.postShake.preRender()
    gl.clearColor(0, 0, 0, 1)
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT)
    gl.colorMask(true, true, true, true)
    this.word.render()
    this.postShake.postRender()

    this.postShake.render()

    this.postRender()
  }

  postRender () {
    this.gl.viewport(0, 0, this.width, this.height)
    // this.grain.render()
  }
}
export default Scene
